import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight, Building2, Check, CircleCheck, GraduationCap, Layers,
  MailOpen, School, Sparkles,
} from 'lucide-react';
import { ComingSoonButton } from '@kollez/design-system';

/**
 * /register — enrolment-card ledger format.
 *
 * The page reads like an enrolment card being filled into the Kollez
 * register. Big serif masthead ("ENROLMENT · Vol. 1"), a question
 * H1 ("Which side of the register are you signing?"), then four
 * numbered persona tabs — each with a distinct chalk-yellow /
 * terracotta / slate-blue / cream accent, a serif label, a preview
 * of the first ledger entry they'll write, a per-persona time-to-fill
 * estimate, and a primary CTA.
 *
 * Design spec: docs/design/marketing/_per-page-design-language.md
 * (register gets the enrolment-card / ledger-tabs format).
 */

const NAMES: Record<string, string> = {
  pilot: 'free Pilot',
  college: 'College plan',
  group: 'Group plan',
  assess: 'Assessments-only plan',
};

const CODES: Record<string, string> = {
  'GIT-2026':    'Godavari Institute of Technology, Rajahmundry',
  'GIT-RJY-26':  'Godavari Institute of Technology, Rajahmundry',
  'GEC-2026':    'Godavari Engineering College, Kakinada',
  'GCP-2026':    'GCP Amalapuram (College of Pharmacy)',
};

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.6 13.2l7.9 6.2C12.4 13.6 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.4 5.7c4.3-4 6.9-9.9 6.9-17.1z" />
    <path fill="#FBBC05" d="M10.5 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.2C.9 16.5 0 20.1 0 24s.9 7.5 2.6 10.8l7.9-6.2z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2.1 1.4-4.8 2.2-8.5 2.2-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.2C6.6 42.6 14.6 48 24 48z" />
  </svg>
);

/* ────────── page ────────── */

export function RegisterPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const plan = params.get('plan') ?? '';
  const [code, setCode] = useState('');
  const [codeErr, setCodeErr] = useState<string | null>(null);
  const [invCode, setInvCode] = useState('');

  const codeStatus = useMemo(() => {
    const v = code.trim().toUpperCase();
    if (!v) return { kind: 'idle' as const, label: 'Your placement office shares it on WhatsApp or the notice board.' };
    if (CODES[v]) return { kind: 'ok' as const, label: CODES[v]! };
    if (v.length >= 8) return { kind: 'err' as const, label: "We don't recognise that code. Check with your placement office — codes change each year." };
    return { kind: 'idle' as const, label: 'Your placement office shares it on WhatsApp or the notice board.' };
  }, [code]);

  useEffect(() => { setCodeErr(null); }, [code]);

  function submitCode(e: React.FormEvent) {
    e.preventDefault();
    const v = code.trim().toUpperCase();
    if (!CODES[v]) { setCodeErr(v ? "That code isn't valid." : "Enter your college code, or tap 'I don't have a code'."); return; }
    nav(`/register/student?code=${encodeURIComponent(v)}`);
  }

  const showPlanBanner = plan && NAMES[plan];
  const instHref = `/register/institution?plan=${plan || 'pilot'}`;
  const instLabel = plan === 'pilot' || !plan ? 'Start free pilot' : `Continue with ${NAMES[plan] ?? 'plan'}`;

  const today = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <main
      id="main"
      className="mx-auto"
      style={{ maxWidth: 1080, padding: '48px 20px 96px' }}
    >
      {/* ENROLMENT MASTHEAD */}
      <header
        className="pb-4 flex items-baseline justify-between flex-wrap gap-2"
        style={{ borderBottom: '1px solid rgb(var(--line-secondary))' }}
      >
        <div className="flex items-baseline gap-3 flex-wrap">
          <p
            className="uppercase tabular-nums m-0"
            style={{
              fontSize: '.75rem',
              letterSpacing: '.18em',
              color: 'rgb(var(--ink-tertiary))',
            }}
          >
            Enrolment · Vol. 1
          </p>
          <span style={{ color: 'rgb(var(--ink-tertiary))' }}>·</span>
          <p
            className="italic m-0"
            style={{
              fontFamily: 'Newsreader, Georgia, serif',
              fontSize: '.8125rem',
              color: 'rgb(var(--ink-tertiary))',
            }}
          >
            The register of Indian education
          </p>
        </div>
        <p
          className="uppercase tabular-nums m-0"
          style={{
            fontSize: '.6875rem',
            letterSpacing: '.14em',
            color: 'rgb(var(--ink-tertiary))',
          }}
        >
          {today}
        </p>
      </header>
      <div className="mt-6" />

      {/* H1 + subhead */}
      <section className="mt-10 grid gap-4 max-w-3xl">
        <p
          className="m-0 uppercase font-medium flex items-center gap-2"
          style={{
            fontSize: '.75rem',
            letterSpacing: '.14em',
            color: 'rgb(var(--ink-accent))',
          }}
        >
          <Sparkles size={12} />
          Sign the register
        </p>
        <h1
          className="m-0 text-ink-primary"
          style={{
            fontFamily: 'Newsreader, Georgia, serif',
            fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
            lineHeight: 1.05,
            fontWeight: 500,
            letterSpacing: '-0.01em',
          }}
        >
          Which side of the register<br />
          are you signing?
        </h1>
        <p className="m-0 text-lg leading-8 text-ink-secondary" style={{ maxWidth: '58ch' }}>
          Kollez is one platform for education, kept honest by three sides.
          Pick the one that describes you — Nexie will meet you when you finish.
          Students and hiring partners are free; institutions start with a
          free pilot or a plan.
        </p>
      </section>

      {/* SSO STRIP — same account across email / mobile / Google */}
      <section className="mt-8 max-w-2xl">
        <ComingSoonButton
          label="Continue with Google"
          icon={<GoogleIcon />}
          reason="Google SSO is being wired. Pick your door below and continue with email/mobile — the same account works once Google is enabled."
        />
        <p
          className="helper mt-2 m-0"
          style={{ color: 'rgb(var(--ink-tertiary))', fontSize: '.75rem' }}
        >
          Same account for email, mobile, and Google.
        </p>
      </section>

      {/* PLAN BANNER (if arriving from /pricing) */}
      {showPlanBanner && (
        <section className="mt-8 max-w-3xl">
          <div className="notice notice-info">
            <Layers size={16} />
            <div className="flex-1">
              You chose the <b>{NAMES[plan]}</b>. We&apos;ll carry it into your institution sign-up.
            </div>
            <Link to="/pricing" className="btn btn-ghost btn-sm">Change</Link>
          </div>
        </section>
      )}

      {/* FOUR TABS — the enrolment options */}
      <section
        className="mt-10 pt-6"
        style={{ borderTop: '1px solid rgb(var(--line-secondary))' }}
      >
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-6">
          <p
            className="m-0 uppercase font-medium"
            style={{
              fontSize: '.6875rem',
              letterSpacing: '.16em',
              color: 'rgb(var(--ink-tertiary))',
            }}
          >
            Four ways in · pick one
          </p>
          <p
            className="m-0 italic"
            style={{
              fontFamily: 'Newsreader, Georgia, serif',
              fontSize: '.8125rem',
              color: 'rgb(var(--ink-tertiary))',
            }}
          >
            Every entry becomes a page.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          {/* STUDENT — chalk-yellow tab */}
          <PersonaTab
            index="01"
            eyebrow="I'm a student"
            headline="Free forever. Nexie in your pocket."
            time="about 3 min"
            costLabel="Free"
            costTone="success"
            accent={{ edge: 'rgba(232,212,122,0.6)', tint: 'rgba(232,212,122,0.06)', text: 'rgb(var(--ink-warning))' }}
            icon={<GraduationCap size={20} />}
            previewTitle="Your first ledger entry"
            previewLines={[
              { at: 'Now', what: 'Create profile' },
              { at: '+3 min', what: 'Verify college' },
              { at: '+15 min', what: 'See eligible drives' },
              { at: 'This week', what: 'First Nexie chat' },
            ]}
          >
            <form className="grid gap-2" onSubmit={submitCode} noValidate>
              <label className="grid gap-1.5">
                <span className="label text-xs">College code</span>
                <div className="flex gap-2">
                  <input
                    className="input flex-1"
                    placeholder="e.g. GIT-2026"
                    autoComplete="off"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    style={{ textTransform: 'uppercase' }}
                  />
                  <button className="btn btn-primary" type="submit">Continue</button>
                </div>
                <span
                  className="helper"
                  style={
                    codeStatus.kind === 'ok'
                      ? { color: 'rgb(var(--ink-success))' }
                      : codeErr || codeStatus.kind === 'err'
                      ? { color: 'rgb(var(--ink-danger))' }
                      : undefined
                  }
                >
                  {codeStatus.kind === 'ok' && (
                    <CircleCheck size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: 'text-bottom' }} />
                  )}
                  {codeErr ?? codeStatus.label}
                </span>
              </label>
            </form>
            <Link className="link text-sm" to="/register/student">
              I don&apos;t have a code
            </Link>
          </PersonaTab>

          {/* INSTITUTION — slate-blue tab */}
          <PersonaTab
            index="02"
            eyebrow="I run a college or group"
            headline="One register. Fifty colleges. One number at 8 AM."
            time="about 6 min"
            costLabel="Free pilot or plan"
            costTone="info"
            accent={{ edge: 'rgba(150,200,220,0.55)', tint: 'rgba(150,200,220,0.06)', text: 'rgb(var(--ink-accent))' }}
            icon={<School size={20} />}
            primary={Boolean(showPlanBanner)}
            previewTitle="Your first ledger entry"
            previewLines={[
              { at: 'Now',        what: 'Verify GSTIN + institution' },
              { at: '1 working day', what: 'Kollez reviews + approves' },
              { at: '3-4 days',   what: 'One-college pilot live' },
              { at: '2-3 weeks',  what: 'Group-wide rollout' },
            ]}
          >
            <p className="m-0 text-sm text-ink-secondary">
              Principals, directors, placement heads, group admins. Nexie drafts
              your NIRF, NAAC, AICTE. 10-minute undo on every offer.
            </p>
            <div className="flex gap-2 flex-wrap">
              <Link className="btn btn-primary" to={instHref}>{instLabel}</Link>
              <Link className="btn btn-outline" to="/pricing">See plans</Link>
            </div>
          </PersonaTab>

          {/* PARTNER — terracotta tab */}
          <PersonaTab
            index="03"
            eyebrow="I hire freshers"
            headline="One post, seven partner campuses. Ranked, cited."
            time="about 4 min"
            costLabel="Free from partner campuses"
            costTone="success"
            accent={{ edge: 'rgba(240,138,138,0.55)', tint: 'rgba(240,138,138,0.06)', text: 'rgb(var(--ink-danger))' }}
            icon={<Building2 size={20} />}
            previewTitle="Your first ledger entry"
            previewLines={[
              { at: 'Now',      what: 'Verify company + domain' },
              { at: '1 day',    what: 'Kollez approves' },
              { at: '+30 min',  what: 'Post first opening' },
              { at: 'Same week', what: 'First shortlist ready' },
            ]}
          >
            <p className="m-0 text-sm text-ink-secondary">
              Campus recruiters, training providers, mentors, hackathon hosts.
              Nexie ranks the shortlist with reasons — you approve every message.
            </p>
            <Link className="btn btn-primary justify-self-start" to="/register/partner">
              Register as a partner
            </Link>
          </PersonaTab>

          {/* INVITED — cream tab */}
          <PersonaTab
            index="04"
            eyebrow="I was invited"
            headline="Your college added your name. Accept and go."
            time="about 2 min"
            costLabel="Sent by your college"
            costTone="neutral"
            accent={{ edge: 'rgba(255,248,234,0.28)', tint: 'rgba(255,248,234,0.03)', text: 'rgb(var(--ink-secondary))' }}
            icon={<MailOpen size={20} />}
            previewTitle="Your first ledger entry"
            previewLines={[
              { at: 'Now',      what: 'Enter invite code' },
              { at: '+1 min',   what: 'Set your password' },
              { at: '+1 min',   what: 'Two-step sign-in' },
              { at: 'Immediately', what: 'Into your workspace' },
            ]}
          >
            <p className="m-0 text-sm text-ink-secondary">
              Placement officers, faculty, heads of department, principals. Open
              the invitation email and tap <b>Accept</b>, or enter the code here.
            </p>
            <div className="flex gap-2">
              <input
                className="input flex-1"
                placeholder="Invitation code, e.g. K7Q-P2M"
                value={invCode}
                onChange={(e) => setInvCode(e.target.value)}
              />
              <Link
                className="btn btn-outline"
                to={invCode ? `/accept-invite?code=${encodeURIComponent(invCode.trim())}` : '/accept-invite'}
              >
                Accept
              </Link>
            </div>
          </PersonaTab>
        </div>
      </section>

      {/* ETHICS + LEGAL — the enrolment footer */}
      <section
        className="mt-12 pt-6 pb-6"
        style={{ borderTop: '1px solid rgb(var(--line-secondary))' }}
      >
        <div className="grid gap-4 md:grid-cols-[1fr_auto] items-baseline">
          <p className="helper m-0" style={{ maxWidth: '78ch' }}>
            By creating an account you agree to the{' '}
            <Link className="link" to="/terms">Terms</Link> and{' '}
            <Link className="link" to="/privacy">Privacy notice</Link>.
            We process personal data under India&apos;s DPDP Act, 2023.
            Students under 18 need a parent&apos;s consent, which we ask for
            during sign-up.
          </p>
          <ul
            className="flex flex-wrap gap-x-3 gap-y-1 m-0"
            style={{
              listStyle: 'none',
              padding: 0,
              fontSize: '.6875rem',
              letterSpacing: '.06em',
              color: 'rgb(var(--ink-tertiary))',
            }}
          >
            <li>DPDP Act 2023</li>
            <li>·</li>
            <li>SOC 2 · ISO 27001</li>
            <li>·</li>
            <li>Data stays in India</li>
          </ul>
        </div>
      </section>

      {/* Existing customer footer link */}
      <p className="helper text-center mt-8 m-0" style={{ color: 'rgb(var(--ink-tertiary))' }}>
        Already have an account?{' '}
        <Link className="link" to="/login">
          Sign in <ArrowRight size={12} className="inline" />
        </Link>
      </p>
    </main>
  );
}

/* ────────── sub-components ────────── */

interface AccentPalette { edge: string; tint: string; text: string }

interface PreviewLine { at: string; what: string }

function PersonaTab({
  index, eyebrow, headline, time, costLabel, costTone, accent, icon,
  previewTitle, previewLines, primary = false, children,
}: {
  index: string;
  eyebrow: string;
  headline: string;
  time: string;
  costLabel: string;
  costTone: 'success' | 'info' | 'warning' | 'neutral';
  accent: AccentPalette;
  icon: React.ReactNode;
  previewTitle: string;
  previewLines: PreviewLine[];
  primary?: boolean;
  children: React.ReactNode;
}) {
  const costBg =
    costTone === 'success' ? 'rgb(var(--bg-success))' :
    costTone === 'warning' ? 'rgb(var(--bg-warning))' :
    costTone === 'info'    ? 'rgb(var(--bg-info))'    :
                             'rgb(var(--bg-secondary))';
  const costFg =
    costTone === 'success' ? 'rgb(var(--ink-success))' :
    costTone === 'warning' ? 'rgb(var(--ink-warning))' :
    costTone === 'info'    ? 'rgb(var(--ink-accent))'  :
                             'rgb(var(--ink-tertiary))';

  return (
    <article
      className="relative overflow-hidden"
      style={{
        borderRadius: 12,
        border: `1px solid ${accent.edge}`,
        background: accent.tint,
        padding: '24px 26px',
        boxShadow: primary ? `0 0 0 1px ${accent.edge}` : undefined,
      }}
    >
      {/* Tab number in the corner */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          right: 18,
          top: 14,
          fontFamily: 'Newsreader, Georgia, serif',
          fontSize: '2rem',
          fontWeight: 500,
          lineHeight: 1,
          color: accent.edge,
          opacity: 0.9,
          letterSpacing: '-0.02em',
        }}
      >
        {index}
      </span>

      <div className="grid gap-4">
        {/* Head */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span
              className="flex items-center justify-center rounded"
              style={{
                width: 32, height: 32,
                background: accent.edge,
                color: 'rgb(var(--ink-primary))',
              }}
            >
              {icon}
            </span>
            <p
              className="m-0 font-medium"
              style={{
                fontSize: '.8125rem',
                letterSpacing: '.02em',
                color: accent.text,
              }}
            >
              {eyebrow}
            </p>
          </div>
          <h3
            className="m-0"
            style={{
              fontFamily: 'Newsreader, Georgia, serif',
              fontSize: '1.375rem',
              lineHeight: 1.15,
              fontWeight: 500,
              letterSpacing: '-0.005em',
            }}
          >
            {headline}
          </h3>
          <div className="flex items-center gap-2 mt-2 text-xs flex-wrap">
            <span
              className="px-2 py-0.5 rounded font-medium uppercase tracking-wider"
              style={{
                fontSize: '.6875rem',
                background: costBg,
                color: costFg,
              }}
            >
              {costLabel}
            </span>
            <span style={{ color: 'rgb(var(--ink-tertiary))' }}>·</span>
            <span style={{ color: 'rgb(var(--ink-tertiary))' }}>{time}</span>
          </div>
        </div>

        {/* Interactive body */}
        <div className="grid gap-3">
          {children}
        </div>

        {/* Register-entry preview */}
        <div
          className="rounded-md p-3"
          style={{
            background: 'rgb(var(--bg-primary))',
            border: '1px solid rgb(var(--line-tertiary))',
          }}
        >
          <p
            className="m-0 mb-2 uppercase font-medium"
            style={{
              fontSize: '.625rem',
              letterSpacing: '.14em',
              color: 'rgb(var(--ink-tertiary))',
            }}
          >
            {previewTitle}
          </p>
          <ol className="grid gap-1.5">
            {previewLines.map((p, i) => (
              <li key={i} className="grid grid-cols-[70px_1fr_auto] gap-3 items-baseline text-xs">
                <span
                  className="tabular-nums font-medium"
                  style={{ color: 'rgb(var(--ink-tertiary))' }}
                >
                  {p.at}
                </span>
                <span style={{ color: 'rgb(var(--ink-primary))' }}>{p.what}</span>
                <Check size={10} style={{ color: 'rgb(var(--ink-tertiary))', opacity: 0.4 }} />
              </li>
            ))}
          </ol>
        </div>
      </div>
    </article>
  );
}
